import React, { useState, useEffect } from 'react'

import styled from 'styled-components'
import Joi from 'joi'

import JobExperienceListItem from './JobExperienceListItem'
import JobExperienceForm from './JobExperienceForm'
import EmptyWrapper from '../../../../../../components/EmptyWrapper'

import axiosInstance from '../../../../../../services/config/AxiosIntance'

const JobExperience = ({
  onChange,
  jobExperience,
  setJobExperience,
  jobExperiences,
  setJobExperiences,
  fieldErrors,
  setFieldErrors,
}) => {
  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)

  const [applicantIndustryList, setApplicantIndustryList] = useState([])

  const defaultFields = () =>
    setJobExperience({
      id: Date.now(),
      jobTitle: '',
      companyName: '',
      companyLocation: '',
      industry: applicantIndustryList[0]?.label,
      jobDescriptionHtml: '',
      workType: 'Permanent Work From Home',
      experienceCurrentStatus: 'No longer employed',
      experienceFrom: new Date().toISOString().split('T')[0],
      experienceTo: new Date().toISOString().split('T')[0],
    })

  const validateData = (payload) => {
    const educationalBackgroundSchema = Joi.object({
      id: Joi.alternatives()
        .try(Joi.number(), Joi.string())
        .label('Id')
        .required(),
      jobTitle: Joi.string().label('Job Title').required(),
      companyName: Joi.string().label('Company Name').required(),
      companyLocation: Joi.string().label('Company Location').required(),
      industry: Joi.alternatives()
        .try(
          Joi.string().label('Industry').required(),
          Joi.object({
            value: Joi.number().required(),
            label: Joi.string().required(),
          }).label('Industry')
        )
        .required(),
      jobDescriptionHtml: Joi.string()
        .label('Job Description')
        .custom((value, helpers) => {
          // If no value provided
          if (!value) {
            return helpers.error('any.empty')
          }

          // Strip HTML tags and whitespace
          const plainText = value.replace(/<[^>]*>/g, '').trim()

          // If there's no meaningful text, throw an error
          if (!plainText) {
            return helpers.error('any.empty')
          }

          // Otherwise, return the value as valid
          return value
        })
        .required()
        .messages({
          'any.empty': '"Job Description" cannot be empty',
          'any.required': '"Job Description" is required',
        }),

      workType: Joi.string().label('Work Type').required(),
      experienceCurrentStatus: Joi.string().label('Current Status').required(),
      experienceFrom: Joi.date().label('Date From').required().messages({
        'date.base': '"Date From" must be a valid date',
        'any.required': '"Date From" is required',
      }),
      experienceTo: Joi.date()
        .label('Date To')
        .required()
        .custom((value, helpers) => {
          const from = helpers.state.ancestors[0].experienceFrom
          if (!from) return value

          const fromDate = new Date(from)
          const toDate = new Date(value)

          const diffInDays = (toDate - fromDate) / (1000 * 60 * 60 * 24)

          if (diffInDays < 1) {
            return helpers.error('date.minDays')
          }

          return value
        })
        .messages({
          'date.base': '"Date To" must be a valid date',
          'any.required': '"Date To" is required',
          'date.minDays': '"Date To" must be at least 1 day after "Date From"',
        }),
    })

    const result = educationalBackgroundSchema.validate(payload, {
      abortEarly: false,
    })

    if (result.error) {
      const { error } = result
      const fieldNames = Object.keys(payload)
      const updatedErrors = fieldNames.reduce((acc, fieldName) => {
        acc[fieldName] =
          error?.details
            .find((detail) => detail.context.key === fieldName)
            ?.message.replace(/"/g, '') || ''
        return acc
      }, {})

      setFieldErrors((prev) => ({
        ...prev,
        jobExperience: { ...updatedErrors },
      }))
      return false
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        jobExperience: {},
      }))
      return true
    }
  }

  const handleEdit = (data) => {
    setShowForm(true)
    setJobExperience(data)
    setIsEdit(true)
  }

  const handleDelete = (id) => {
    setShowForm(false)
    const updatedJobExperiences = jobExperiences.filter((e) => e.id !== id)
    setJobExperiences(updatedJobExperiences)
    setIsEdit(false)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setIsEdit(false)
  }

  const handleAddJobExperience = () => {
    if (!showForm) {
      setShowForm(true)
      defaultFields()
    } else {
      if (!validateData(jobExperience)) return

      if (isEdit) {
        const selected = jobExperiences.filter(
          (e) => e.id === jobExperience?.id
        )[0]

        const updated = {
          ...jobExperience,
          id: selected.id,
          industry: jobExperience?.industry?.label || jobExperience?.industry,
        }

        setJobExperiences(
          jobExperiences.map((e) => (e.id === updated.id ? updated : e))
        )
        setIsEdit(false)
      } else {
        const newExperience = [
          ...jobExperiences,
          {
            id: Date.now(),
            ...jobExperience,
            industry:
              jobExperience.industry?.label || applicantIndustryList[0]?.label,
            experienceFrom: new Date(jobExperience.experienceFrom)
              .toISOString()
              .split('T')[0],
            experienceTo: new Date(jobExperience.experienceTo)
              .toISOString()
              .split('T')[0],
          },
        ].sort((a, b) => new Date(a.experienceTo) - new Date(b.experienceTo)) // ascending by "experienceTo"

        setJobExperiences(newExperience)
      }

      defaultFields()
      setShowForm(false)
    }
  }

  const handleChange = (name, value) => {
    onChange({ [name]: value })
  }

  useEffect(() => {
    axiosInstance
      .get('/applicant-industries')
      .then((response) => {
        const data = response?.data?.data || []
        const formattedApplicantIndustries = data.map((industry) => ({
          value: industry,
          label: industry,
        }))
        setApplicantIndustryList(formattedApplicantIndustries)
      })
      .catch((error) => {
        console.error('Error fetching educational levels:', error)
      })
  }, [])
  return (
    <>
      <JobExperienceListItemContainer>
        {jobExperiences?.length > 0 ? (
          jobExperiences.map((data, index) => (
            <JobExperienceListItem
              key={index}
              id={data.id}
              jobTitle={data.jobTitle}
              companyName={data.companyName}
              companyLocation={data.companyLocation}
              industry={data.industry}
              experienceCurrentStatus={data.experienceCurrentStatus}
              workType={data.workType}
              jobDescriptionHtml={data.jobDescriptionHtml}
              experienceFrom={data.experienceFrom}
              experienceTo={data.experienceTo}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        ) : (
          <EmptyWrapper message="No job experiences." />
        )}
      </JobExperienceListItemContainer>
      <JobExperienceForm
        jobExperience={jobExperience}
        onChange={handleChange}
        showForm={showForm}
        onAddJobExperience={handleAddJobExperience}
        onClose={handleCloseForm}
        isEdit={isEdit}
        applicantIndustryList={applicantIndustryList}
        fieldErrors={fieldErrors}
      />
    </>
  )
}

const JobExperienceListItemContainer = styled.div`
  max-height: 400px;
  overflow: auto;
  margin-bottom: 1rem;
`

export default JobExperience
